import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	AlertDialog,
	useConfirmationDialog,
	ContextMenu,
	useContextMenu,
	TreeView,
	TreeViewItem,
	TreeViewHandle
} from "client/Components";
import {
	AlertManager,
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	deleteEmailFolder,
	EagerLoadedChildren,
	ExchangeAccountModel,
	FolderCounters,
	IFolderViewModel,
	LazyLoadedChildren,
	MessageReadState
} from "client/Api/Email/email";
import {
	Action,
	ActionGroup,
	ActionKind
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { useEmailCreateFolderDialog } from "../Dialogs/EmailCreateFolderDialog";
import { useEmailRenameFolderDialog } from "../Dialogs/EmailRenameFolderDialog";
import {
	convertEagerLoadedFolderToItem,
	findDeletedFolderInAccount,
	UpdateEmailFoldersMode,
	UpdateEmailFoldersModeKind,
	updateTreeStructure
} from "./updateTreeStructure";
import { Coordinate } from "client/Misc/commonTypes";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import {
	ensureNotNil,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { FolderContentEmailTreeView, FolderContentEmailTreeViewProps } from "./FolderContentEmailTreeView";
import { emailRouting, SelectedFolderWithMailbox } from "../Email";
import { EMAIL_DRAGGABLE_DATA_IDENTIFIER, EmailDraggableData } from "../dragAndDrop";
import { never } from "client_legacy/ndaw/errors";
import { getClientId } from "client/Misc/clientId";
import {
	convertToExchangeAccountItem,
	convertToExchangeFolderItem,
	ExchangeAccountItem,
	ExchangeFolderItem,
	ExchangeItem,
	ExchangeFolderChildrentState,
	findNextFolderToSelect,
	FolderBranch,
	isFolderUnderDeleteFolder,
	tryToFindFolder
} from "../common";

export type EmailTreeViewProps = {
	accountItems: readonly ExchangeAccountItem[];
	selectedFolderWithMailbox: SelectedFolderWithMailbox | undefined;
	onSidebarNavigation?(): void;
	onMoveEmailsToFolder?(
		destinationFolder: IFolderViewModel<LazyLoadedChildren>,
		emails: readonly EmailIdWithIsReadStatus[],
		smtpAddress: string | null
	): void;
	counters?: Map<string, FolderCounters>;
	initialExpandedRootFolderId?: string;
	onIsLoadingChange(isLoading: boolean): void;
	onFetchFolders: (
		smtpAddress: string,
		folderId: string,
		shouldExcludeSelf: boolean
	) => Promise<readonly ExchangeAccountItem[]>;
} & ({
	doesSupportContextActions: true;
	onAccountsChange: (accounts: readonly ExchangeAccountItem[]) => void;
	onReadStatusChange(
		smtpAddress: string,
		folderId: string,
		readStatus: MessageReadState
	): void;
	setFolderCounters(folderCounters: FolderCounters): void;
	onNavigateToFolder: (smtpAddress: string, folderId: string) => void;
} | {
	doesSupportContextActions?: false;
	onAccountsChange?: undefined;
	onReadStatusChange?: undefined;
	setFolderCounters?: undefined;
	onNavigateToFolder?: undefined;
});

export const EmailTreeView = (props: EmailTreeViewProps): ReactElement => {
	const {
		accountItems,
		onAccountsChange,
		selectedFolderWithMailbox,
		onSidebarNavigation,
		doesSupportContextActions,
		onReadStatusChange,
		onIsLoadingChange,
		onMoveEmailsToFolder,
		setFolderCounters,
		onFetchFolders,
		counters,
		onNavigateToFolder,
		initialExpandedRootFolderId
	} = props;

	const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);
	const [folderMenuContext, setFolderMenuContext] = useState<EmailFolderActionModel | null>(null);

	const [emailCreateFolderDialog, openEmailCreateFolderDialog] = useEmailCreateFolderDialog();
	const [emailRenameFolderDialog, openEmailRenameFolderDialog] = useEmailRenameFolderDialog();
	const [emailDeleteFolderConfirmationDialog, openEmailDeleteFolderConfirmationDialog] = useConfirmationDialog();

	const containerRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const treeViewRef: RefObject<TreeViewHandle<ExchangeItem> | null> = useRef<TreeViewHandle<ExchangeItem>>(null);
	const hasExpandedInitialSelectionRef: RefObject<boolean> = useRef<boolean>(false);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [contextMenuHandler, contextMenuProps] = useContextMenu(containerRef);
	const selectedFolder: IFolderViewModel<LazyLoadedChildren> | undefined = selectedFolderWithMailbox?.folder;

	const selectedItem = useMemo(
		(): ExchangeItem | undefined => {
			if (!selectedFolderWithMailbox) {
				return undefined;
			}

			return {
				kind: "folder",
				folder: selectedFolderWithMailbox.folder,
				smtpAddress: selectedFolderWithMailbox
					.account
					.emailAddress,
				childrenState: { status: "pending" }
			};
		},
		[selectedFolderWithMailbox]
	);

	const items = useMemo(
		(): readonly TreeViewItem<ExchangeItem>[] => {
			const convertFolders = (
				folders: readonly ExchangeFolderItem[]
			): readonly TreeViewItem<ExchangeItem>[] => {
				return folders.map(
					(folder: ExchangeFolderItem): TreeViewItem<ExchangeItem> => {
						const {
							childrenState,
							folder: model,
							smtpAddress
						} = folder;

						const title: string = childrenState.status === "error"
							? localize("_FailedToLoadTheFolder")
							: model.name;

						const status: FolderContentEmailTreeViewProps["status"] | null = itemChildrenStatusToContentStatusMap[childrenState.status];

						return {
							value: folder,
							content: () => (
								<FolderContent
									folder={model}
									counters={counters}
									status={status ?? undefined}
								/>
							),
							children: childrenState.status === "success"
								? convertFolders(childrenState.children)
								: model.children.hasChildren && childrenState.status !== "error"
									? "lazy"
									: null,
							title,
							navigation: emailRouting.page({
								queryParams: {
									folderToOpen: model.exchangeFolderId,
									mailboxToOpen: smtpAddress
								}
							})
						};
					}
				);
			};

			return accountItems.map(
				(account: ExchangeAccountItem): TreeViewItem<ExchangeItem> => {
					const {
						account: model,
						isFaulted
					} = account;

					return {
						value: account,
						content: () => (
							<AccountContent
								account={model}
								counters={counters}
							/>
						),
						title: model.emailAddress,
						children: !isFaulted
							? convertFolders(account.folders)
							: null
					};
				}
			);
		},
		[accountItems, counters]
	);

	const updateFolderModeHandler = useMemo(
		(): ((smtpAddress: string, folderId: string, mode: UpdateEmailFoldersMode) => void) | undefined => {
			if (!doesSupportContextActions) {
				return undefined;
			}

			return async (smtpAddress: string, folderId: string, mode: UpdateEmailFoldersMode): Promise<void> => {
				const updatedAccountItems: readonly ExchangeAccountItem[] = await onFetchFolders(
					smtpAddress,
					folderId,
					true
				);

				const updatedAccounts: readonly ExchangeAccountItem[] = updateTreeStructure(
					updatedAccountItems,
					smtpAddress,
					folderId,
					mode
				);

				onAccountsChange(updatedAccounts);
			};
		},
		[
			doesSupportContextActions,
			onAccountsChange,
			onFetchFolders
		]
	);

	const readStatusChangeHandler = useCallback(
		(
			smtpAddress: string,
			folderId: string,
			readStatus: MessageReadState,
			onReadStatusChange: (
				smtpAddress: string,
				folderId: string,
				readStatus: MessageReadState
			) => void,
			onIsLoadingChange: (isLoading: boolean) => void
		): void => {
			try {
				onIsLoadingChange(true);
				onReadStatusChange(
					smtpAddress,
					folderId,
					readStatus
				);
				onIsLoadingChange(false);
			} catch (e) {
				onIsLoadingChange(false);
			}
		},
		[]
	);

	const itemClickHandler = useCallback(
		(item: ExchangeItem): void => {
			switch (item.kind) {
				case "account":
					if (item.account
						.isFaulted
					) {
						setErrorDialogMessage(
							localize("_CannotLoadMailboxCheckPermissions")
						);
					} else {
						treeViewRef.current
							?.toggleNodes([item]);
					}
					break;
				case "folder":
					if (isMobile) {
						onSidebarNavigation?.();
					}
					break;
				default:
					never(item);
			}
		},
		[isMobile, onSidebarNavigation]
	);

	const expandToFolder = useCallback(
		async (account: ExchangeAccountModel, folderId: string): Promise<void> => {
			const smtpAddress: string = account.emailAddress;
			const treeView: TreeViewHandle<ExchangeItem> = ensureNotNil(treeViewRef.current);
			const updatedAccountItems: readonly ExchangeAccountItem[] = await onFetchFolders(
				smtpAddress,
				folderId,
				false
			);

			const folderResult: TryResult<FolderBranch> = tryToFindFolder(
				updatedAccountItems,
				smtpAddress,
				folderId
			);

			if (!folderResult.wasSuccessful) {
				throw new Error(`Could not find folder with id ${folderId}`);
			}

			const { branch, folder } = folderResult.result;
			const branchExcludeSelf: readonly IFolderViewModel<LazyLoadedChildren>[] = branch
				.filter(x => x.exchangeFolderId !== folderId);

			treeView.expandNodes([
				convertToExchangeAccountItem(account),
				...branchExcludeSelf.map(
					(x): ExchangeFolderItem => convertToExchangeFolderItem(
						smtpAddress,
						x
					)
				)
			]);

			treeView.scrollIntoView(
				convertToExchangeFolderItem(
					smtpAddress,
					folder
				)
			);
		},
		[onFetchFolders]
	);

	useEffect(
		(): (() => void) | void => {
			if (!selectedFolderWithMailbox || hasExpandedInitialSelectionRef.current) {
				return;
			}

			const {
				folder,
				account
			} = selectedFolderWithMailbox;

			expandToFolder(
				account,
				folder.exchangeFolderId
			);

			hasExpandedInitialSelectionRef.current = true;
		},
		[expandToFolder, selectedFolderWithMailbox]
	);

	const deleteHandler = useCallback(
		async (
			accountItems: readonly ExchangeAccountItem[],
			smtpAddress: string,
			folderIdToDelete: string,
			isUnderDeleteFolder: boolean,
			onNavigateToFolder: (smtpAddress: string, folderId: string) => void,
			onUpdateTreeStructure: (smtpAddress: string, folderId: string, mode: UpdateEmailFoldersMode) => void,
			onIsLoadingChange: (isLoading: boolean) => void
		): Promise<void> => {
			if (!folderIdToDelete) {
				return;
			}

			let nextFolder: IFolderViewModel<LazyLoadedChildren> | null = null;

			if (
				selectedFolder
					?.folderId
					?.id === folderIdToDelete
			) {
				nextFolder = findNextFolderToSelect(
					accountItems,
					smtpAddress,
					folderIdToDelete
				);
			}

			try {
				onIsLoadingChange(true);

				await deleteEmailFolder(
					{
						location: {
							smtpAddress: {
								isSet: true,
								value: smtpAddress
							},
							folderId: folderIdToDelete
						},
						moveToDeletedItems: !isUnderDeleteFolder
					},
					globalCancellation
				);

				onIsLoadingChange(false);

				if (isNotNil(nextFolder)) {
					onNavigateToFolder(smtpAddress, nextFolder.exchangeFolderId);
				}

				onUpdateTreeStructure(
					smtpAddress,
					folderIdToDelete,
					{
						kind: UpdateEmailFoldersModeKind.Delete,
						selectedFolder: selectedFolder,
						wasUnderDeleteFolder: isUnderDeleteFolder
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				onIsLoadingChange(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			globalCancellation,
			selectedFolder
		]
	);

	const folderCreatedHandler = useCallback(
		(
			smtpAddress: string,
			parentNodeId: string,
			newFolder: IFolderViewModel<EagerLoadedChildren>,
			setFolderCounters: (folderCounters: FolderCounters) => void,
			onUpdateTreeStructure: (smtpAddress: string, folderId: string, mode: UpdateEmailFoldersMode) => void
		): void => {
			setFolderCounters({
				folderId: newFolder.folderId,
				total: newFolder.totalCount,
				unread: newFolder.unreadCount
			});

			onUpdateTreeStructure(
				smtpAddress,
				parentNodeId,
				{
					kind: UpdateEmailFoldersModeKind.Add,
					newFolder: convertEagerLoadedFolderToItem(
						smtpAddress,
						newFolder
					)
				}
			);
		},
		[]
	);

	const folderActions = useMemo(
		(): readonly EmailFolderAction[] | undefined => {
			if (!doesSupportContextActions || !updateFolderModeHandler) {
				return undefined;
			}

			return getFolderActions({
				markAllAsRead: (smtpAddress: string, id: string): void => {
					readStatusChangeHandler(
						smtpAddress,
						id,
						MessageReadState.Read,
						onReadStatusChange,
						onIsLoadingChange
					);
				},
				markAllAsUnRead: (smtpAddress: string, id: string): void => {
					readStatusChangeHandler(
						smtpAddress,
						id,
						MessageReadState.Unread,
						onReadStatusChange,
						onIsLoadingChange
					);
				},
				createFolder: async (smtpAddress: string, id: string): Promise<void> => {
					const createResult: TryResult<IFolderViewModel<EagerLoadedChildren>> = await openEmailCreateFolderDialog({
						target: {
							kind: "folderId",
							folderId: id,
							mailbox: smtpAddress
						}
					});

					if (createResult.wasSuccessful) {
						folderCreatedHandler(
							smtpAddress,
							id,
							createResult.result,
							setFolderCounters,
							updateFolderModeHandler
						);
					}
				},
				renameFolder: async (data: RenameFolderDialogState): Promise<void> => {
					const renameResult: TryResult<string> = await openEmailRenameFolderDialog({
						smtpAddress: data.smtpAddress,
						folderId: data.id,
						folderName: data.folderName,
					});

					if (renameResult.wasSuccessful) {
						updateFolderModeHandler(
							data.smtpAddress,
							data.id,
							{
								kind: UpdateEmailFoldersModeKind.Rename,
								newName: renameResult.result
							}
						);
					}
				},
				deleteFolder: async (smtpAddress: string, folderId: string): Promise<void> => {
					let updatedAccountItems: readonly ExchangeAccountItem[] = await onFetchFolders(
						smtpAddress,
						folderId,
						true
					);

					const isUnderDeleteFolder: boolean = isFolderUnderDeleteFolder(
						updatedAccountItems,
						smtpAddress,
						folderId
					);

					const deleteConfirmationResult: TryResult<void> = await openEmailDeleteFolderConfirmationDialog({
						title: localize("_DeleteThisItem"),
						confirmText: localize("_Delete"),
						trackingId: "delete-folder-dialog",
						children: isUnderDeleteFolder
							? localize("_DeleteFolderPermanentlyWithContent")
							: localize("_MoveFolderToDeletedItemsWithContent")
					});

					if (!deleteConfirmationResult.wasSuccessful) {
						return;
					}

					if (!isUnderDeleteFolder) {
						const account: ExchangeAccountItem | undefined = updatedAccountItems.find(
							x => x.account.emailAddress === smtpAddress
						);

						if (isNil(account)) {
							throw new Error(`Could not find the account for the mailbox id ${smtpAddress}`);
						}

						const deletedFolderResult: TryResult<IFolderViewModel<LazyLoadedChildren>> = findDeletedFolderInAccount(
							account
						);

						if (!deletedFolderResult.wasSuccessful) {
							throw new Error(`There is no delete folder in account with id ${smtpAddress}`);
						}

						updatedAccountItems = await onFetchFolders(
							smtpAddress,
							deletedFolderResult.result.exchangeFolderId,
							false
						);
					}

					deleteHandler(
						updatedAccountItems,
						smtpAddress,
						folderId,
						isUnderDeleteFolder,
						onNavigateToFolder,
						updateFolderModeHandler,
						onIsLoadingChange
					);
				},
			});
		},
		[
			doesSupportContextActions,
			updateFolderModeHandler,
			readStatusChangeHandler,
			onReadStatusChange,
			onIsLoadingChange,
			openEmailCreateFolderDialog,
			folderCreatedHandler,
			setFolderCounters,
			openEmailRenameFolderDialog,
			openEmailDeleteFolderConfirmationDialog,
			deleteHandler,
			onNavigateToFolder,
			onFetchFolders
		]
	);

	const itemContextMenuHandler = useMemo(
		(): ((item: ExchangeItem, position: Coordinate) => void) | undefined => {
			const availableMenuHandler = (
				item: ExchangeItem,
				position: Coordinate,
				onNavigateToFolder: (smtpAddress: string, folderId: string) => void
			): void => {
				let folderCounters: FolderCounters | undefined = undefined;

				switch (item.kind) {
					case "account":
						if (item.account
							.isFaulted
						) {
							return;
						}

						folderCounters = getAccountCounters(counters, item.account);

						break;
					case "folder":
						const folderId: string = item.folder
							.exchangeFolderId;

						folderCounters = counters?.get(folderId);

						onNavigateToFolder(item.smtpAddress, folderId);
						break;
					default:
						never(item);
				}

				if (!folderCounters) {
					return;
				}

				contextMenuHandler(position);
				setFolderMenuContext({
					item,
					counters: folderCounters
				});
			};

			if (!doesSupportContextActions || !updateFolderModeHandler) {
				return undefined;
			}

			return (item: ExchangeItem, position: Coordinate): void => {
				availableMenuHandler(
					item,
					position,
					onNavigateToFolder
				);
			};
		},
		[
			doesSupportContextActions,
			updateFolderModeHandler,
			contextMenuHandler,
			counters,
			onNavigateToFolder
		]
	);

	const selectedSmtpAddress = useMemo(
		(): string | undefined => selectedFolderWithMailbox
			?.account
			.emailAddress,
		[selectedFolderWithMailbox?.account.emailAddress]
	);

	const dragOverHandler = useCallback(
		(item: ExchangeItem, args: DragOverEventArgs): DragOverEventResult => {
			const { types } = args;

			const isFaulted: boolean = item.kind === "account"
				&& item.account
					.isFaulted;

			if (!onMoveEmailsToFolder
				|| isFaulted
				|| !types.includes(EMAIL_DRAGGABLE_DATA_IDENTIFIER)
				|| (item.kind === "folder" && item.smtpAddress !== selectedSmtpAddress)
				|| (item.kind === "account" && item.account.emailAddress !== selectedSmtpAddress)
			) {
				return { isAccepted: false };
			}

			return {
				isAccepted: true,
				dropEffect: "move"
			};
		},
		[
			selectedSmtpAddress,
			onMoveEmailsToFolder
		]
	);

	const shouldAutoExpandOnLongDragOver = useCallback(
		(item: ExchangeItem): boolean => {
			switch (item.kind) {
				case "account":
					const { account } = item;
					return !account.isFaulted
						&& account.emailAddress === selectedSmtpAddress;
				case "folder":
					return item
						.folder
						.children
						.hasChildren
						&& item.smtpAddress === selectedSmtpAddress;
				default:
					never(item);
			}
		},
		[selectedSmtpAddress]
	);

	const availableDropHandler = useCallback(
		(
			item: ExchangeItem,
			args: DropEventArgs,
			onMoveEmailsToFolder: (
				destinationFolder: IFolderViewModel<LazyLoadedChildren>,
				emails: readonly EmailIdWithIsReadStatus[],
				smtpAddress: string | null
			) => void
		): void => {
			const data: EmailDraggableData | null = args.getJsonData<EmailDraggableData>(EMAIL_DRAGGABLE_DATA_IDENTIFIER);

			if (data === null
				|| item.kind !== "folder"
				|| item.smtpAddress !== data.smtpAddress
			) {
				return;
			}

			onMoveEmailsToFolder(
				item.folder,
				data.emails,
				data.smtpAddress
			);
		},
		[]
	);

	const dropHandler = useMemo(
		(): ((item: ExchangeItem, args: DropEventArgs) => void) | undefined => {
			if (!onMoveEmailsToFolder) {
				return undefined;
			}

			return (item: ExchangeItem, args: DropEventArgs): void => {
				availableDropHandler(item, args, onMoveEmailsToFolder);
			};
		},
		[availableDropHandler, onMoveEmailsToFolder]
	);

	const toggleExpandHandler = useCallback(
		async (item: ExchangeItem): Promise<void> => {
			if (item.kind !== "folder"
				|| item.childrenState.status === "success"
				|| item.childrenState.status === "loading"
			) {
				return;
			}

			const {
				smtpAddress,
				folder
			} = item;

			await onFetchFolders(
				smtpAddress,
				folder.exchangeFolderId,
				false
			);
		},
		[onFetchFolders]
	);

	const initiallyExpandedNodes = useMemo(
		(): readonly ExchangeItem[] => {
			if (isNotNil(initialExpandedRootFolderId)) {
				const rootItem: ExchangeAccountItem | undefined = accountItems.find(
					x => x.account.rootFolderId?.id === initialExpandedRootFolderId
				);

				return rootItem
					? [rootItem]
					: [];
			}

			return [];
		},
		[accountItems, initialExpandedRootFolderId]
	);

	return (
		<>
			<div
				{...setTrackingId("email-tree-view-wrapper")}
				ref={containerRef}
			>
				<TreeView<ExchangeItem>
					ref={treeViewRef}
					trackingId="email-tree-view"
					items={items}
					value={selectedItem}
					compareBy={idSelector}
					onDragOver={dragOverHandler}
					shouldAutoExpandOnLongDragOver={shouldAutoExpandOnLongDragOver}
					onDrop={dropHandler}
					onItemClick={itemClickHandler}
					onContextMenu={itemContextMenuHandler}
					onToggleExpand={toggleExpandHandler}
					initiallyExpandedNodes={initiallyExpandedNodes}
					areContainersSelectable
				/>
			</div>
			{(contextMenuProps && folderMenuContext && folderActions) && (
				<ContextMenu
					{...contextMenuProps}
					actions={folderActions}
					context={folderMenuContext}
					onClose={() => setFolderMenuContext(null)}
					trackingId="email-tree-view-context-menu"
					title={
						folderMenuContext.item.kind === "account"
							? folderMenuContext.item
								.account
								.emailAddress
							: folderMenuContext.item
								.folder
								.name
						}
				/>
			)}
			{errorDialogMessage && (
				<AlertDialog
					title={localize("_Error")}
					confirmText={localize("_Close")}
					onClose={() => setErrorDialogMessage(null)}
					trackingId="email-tree-error-dialog"
				>
					{errorDialogMessage}
				</AlertDialog>
			)}
			{emailCreateFolderDialog}
			{emailRenameFolderDialog}
			{emailDeleteFolderConfirmationDialog}
		</>
	);
};

EmailTreeView.displayName = "EmailTreeView";

export type EmailFolderActionModel = {
	item: ExchangeItem;
	counters: FolderCounters;
};

type EmailFolderAction = Action<EmailFolderActionModel>;

type FolderActions = {
	markAllAsRead(smtpAddress: string, id: string): void;
	markAllAsUnRead(smtpAddress: string, id: string): void;
	createFolder(smtpAddress: string, id: string): void;
	renameFolder(renameFolder: RenameFolderDialogState): void;
	deleteFolder(smtpAddress: string, id: string): void;
};

type RenameFolderDialogState = {
	smtpAddress: string;
	id: string;
	folderName: string;
};

const readStatusGroup: ActionGroup = {
	kind: "regular",
	name: "read-status",
	title: localize("_Status")
};

function getFolderActions(
	folderActions: FolderActions,
): readonly EmailFolderAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "markAllAsRead",
			title: localize("_MarkAllAsRead"),
			canExecute:
				(c: EmailFolderActionModel): boolean =>
					c.item.kind === "folder"
						&& c.counters.unread > 0,
			execute:
				(c: EmailFolderActionModel): void => {
					if (c.item.kind !== "folder") {
						throw new Error("This action can only be executed in context of a folder.");
					}

					folderActions.markAllAsRead(
						c.item
							.smtpAddress,
						c.item
							.folder
							.exchangeFolderId
					);
				},
			icon: "envelope-open",
			group: readStatusGroup
		},
		{
			kind: ActionKind.Regular,
			name: "markAllAsUnRead",
			title: localize("_MarkAllAsUnread"),
			canExecute:
				(c: EmailFolderActionModel): boolean => {
					if (c.item.kind !== "folder") {
						return false;
					}

					const {
						total,
						unread
					} = c.counters;

					return total > 0
						&& unread < total;
				},
			execute:
				(c: EmailFolderActionModel) => {
					if (c.item.kind !== "folder") {
						throw new Error("This action can only be executed in context of a folder.");
					}

					folderActions.markAllAsUnRead(
						c.item
							.smtpAddress,
						c.item
							.folder
							.exchangeFolderId
					);
				},
			icon: "envelope",
			group: readStatusGroup
		},
		{
			kind: ActionKind.Regular,
			name: "new",
			title: localize("_New"),
			canExecute:
				(c: EmailFolderActionModel): boolean => {
					switch (c.item.kind) {
						case "account":
							return c.item
								.account
								.rootFolderActionOptions
								.canCreate;
						case "folder":
							return c.item
								.folder
								.folderActionOptions
								.canCreate;
						default:
							never(c.item);
					}
				},
			execute:
				(c: EmailFolderActionModel) => {
					switch (c.item.kind) {
						case "account":
							folderActions.createFolder(
								c.item
									.account
									.emailAddress,
								ensureNotNil(
									c.item
										.account
										.rootFolderId
								)
									.id
							);
							break;
						case "folder":
							folderActions.createFolder(
								c.item
									.smtpAddress,
								c.item
									.folder
									.exchangeFolderId
							);
							break;
						default:
							never(c.item);
					}

				},
			icon: "plus"
		},
		{
			kind: ActionKind.Regular,
			name: "renameFolder",
			title: localize("_Rename"),
			canExecute:
				(c: EmailFolderActionModel): boolean =>
					c.item.kind === "folder"
					&& c.item
						.folder
						.folderActionOptions
						.canRenameOrMove,
			execute:
				(c: EmailFolderActionModel) => {
					if (c.item.kind !== "folder") {
						throw new Error("This action can only be executed in context of a folder.");
					}

					folderActions.renameFolder({
						smtpAddress: c.item
							.smtpAddress,
						id: c.item
							.folder
							.exchangeFolderId,
						folderName: c.item
							.folder
							.name
					});
				},
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteFolder",
			title: localize("_Delete"),
			canExecute:
				(c: EmailFolderActionModel): boolean =>
					c.item.kind === "folder"
					&& c.item
						.folder
						.folderActionOptions
						.canDelete,
			execute:
				(c: EmailFolderActionModel) => {
					if (c.item.kind !== "folder") {
						throw new Error("This action can only be executed in context of a folder.");
					}

					folderActions.deleteFolder(
						c.item
							.smtpAddress,
						c.item
							.folder
							.exchangeFolderId
					);
				},
			icon: "trash-can"
		}
	];
}

function idSelector(item: ExchangeItem): string {
	switch (item.kind) {
		case "folder":
			return `folder_${item.folder.exchangeFolderId}`;
		case "account":
			if (isNotNil(item.account.rootFolderId)) {
				return `account_${item.account.rootFolderId.id}`;
			}
			else {
				return `account_faulted_${getClientId(item.account)}`;
			}
		default:
			never(item);
	}
}

type AccountContentProps = {
	account: ExchangeAccountModel;
	counters: Map<string, FolderCounters> | undefined;
};

const AccountContent = (props: AccountContentProps): ReactElement => {
	const {
		account,
		counters
	} = props;

	const countersData: FolderCounters | undefined = getAccountCounters(counters, account);

	return (
		<FolderContentEmailTreeView
			folderName={account.emailAddress}
			count={countersData?.unread}
			isCounterBold
		/>
	);
};
AccountContent.displayName = "AccountContent";

type FolderContentProps = {
	folder: IFolderViewModel<LazyLoadedChildren>;
	counters: Map<string, FolderCounters> | undefined;
	status?: FolderContentEmailTreeViewProps["status"];
};

const FolderContent = (props: FolderContentProps): ReactElement => {
	const {
		folder,
		counters,
		status
	} = props;

	const folderCountersData: FolderCounters | undefined = counters?.get(folder.exchangeFolderId);

	let count: number = folderCountersData?.unread ?? folder.unreadCount;
	let isCounterBold: boolean = true;

	if (folder.isDrafts || folder.isJunkMail) {
		count = folderCountersData?.total ?? folder.totalCount;
		isCounterBold = false;
	}

	return (
		<FolderContentEmailTreeView
			folderName={folder.name}
			count={count}
			isCounterBold={isCounterBold}
			status={status}
		/>
	);
};
FolderContent.displayName = "FolderContent";

const itemChildrenStatusToContentStatusMap: Record<ExchangeFolderChildrentState["status"], FolderContentEmailTreeViewProps["status"] | null> = {
	"pending": null,
	"loading": "loading",
	"error": "error",
	"success": "success"
};

function getAccountCounters(
	counters: Map<string, FolderCounters> | undefined,
	account: ExchangeAccountModel
): FolderCounters | undefined {
	if (!counters) {
		return undefined;
	}

	const inboxFolder: IFolderViewModel<LazyLoadedChildren> | undefined = account.folders
		.find(f => f.isInbox);

	if (!inboxFolder) {
		return undefined;
	}

	return counters.get(inboxFolder.exchangeFolderId);
}